'use client';

import { useContext, useEffect, useMemo, useState } from 'react';
import { NotificationContext } from '../../providers/NotificationProvider';
import { enqueueSnackbar } from 'notistack';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import OneSignal from 'react-onesignal';
import { getMobileOperatingSystem } from '../../shared/utils/device';
import CloseIcon from '@mui/icons-material/Close';

type Props = {};

// Notification reminder for PWA and push notification
export const NotificationReminder: React.FC<Props> = ({}) => {
  const { open, hideReminder, type, showReminder } =
    useContext(NotificationContext);
  const [err, setErr] = useState('');
  const [device, setDevice] = useState('Android');

  useEffect(() => {
    const d = getMobileOperatingSystem();
    setDevice(d);
  }, []);

  useEffect(() => {
    if (!!err) {
      setTimeout(() => {
        setErr('');
      }, 4000);
    }
  }, [err]);

  const Desc = useMemo(() => {
    if (device === 'iOS')
      return (
        <Typography variant="body2">
          1. In your Safari browser{' '}
          <span
            style={{
              color: '#43BDCE',
            }}
          >
            menu
          </span>
          , tap the{' '}
          <span
            style={{
              color: '#43BDCE',
            }}
          >
            Share icon
          </span>{' '}
          <br />
          <br />
          2. Choose &apos;{' '}
          <span
            style={{
              color: '#43BDCE',
            }}
          >
            Add to Home Screen
          </span>
          &apos; in the options <br />
          <br />
          3. Open the{' '}
          <span
            style={{
              color: '#43BDCE',
            }}
          >
            Virtuals Protocol app
          </span>{' '}
          on your home screen
        </Typography>
      );
    return (
      <Typography variant="body2">
        1. In your Chrome browser{' '}
        <span
          style={{
            color: '#43BDCE',
          }}
        >
          menu
        </span>
        , tap the{' '}
        <span
          style={{
            color: '#43BDCE',
          }}
        >
          Ellipsis icon
        </span>{' '}
        (looks like three vertical dots)
        <br />
        <br />
        2. Choose &apos;{' '}
        <span
          style={{
            color: '#43BDCE',
          }}
        >
          Install App
        </span>
        &apos; in the options <br />
        <br />
        3. Open the{' '}
        <span
          style={{
            color: '#43BDCE',
          }}
        >
          Virtuals Protocol app
        </span>{' '}
        on your home screen
        <br />
        <br />
        4. Login
      </Typography>
    );
  }, [device]);

  const Content = useMemo(() => {
    if (type === 'notification') {
      return (
        <Stack
          direction="column"
          sx={{
            gap: 3,
          }}
        >
          <Typography variant="subtitle1" textAlign="start" fontWeight={400}>
            Get notified when your contribution status changed or there is new
            contribution to be validated.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={async () => {
              try {
                const nativePermission = Notification.permission;

                if (nativePermission === 'denied') {
                  throw new Error('');
                }

                const result = await Notification.requestPermission();
                if (result === 'granted') {
                  enqueueSnackbar({
                    message: 'You have enabled notifications!',
                    variant: 'success',
                  });
                  hideReminder(type);
                } else {
                  enqueueSnackbar({
                    message:
                      'Please enable notification permission in your app settings.',
                    variant: 'error',
                  });
                  setErr(
                    'Please enable notification permission in your app settings.'
                  );
                }
              } catch (err: any) {
                enqueueSnackbar({
                  message:
                    'Please enable notification permission in your app settings.',
                  variant: 'error',
                });
                setErr(
                  'Please enable notification permission in your app settings.'
                );
              }
            }}
            sx={{
              paddingY: 1,
              width: 'fit-content',
              alignSelf: 'flex-end',
            }}
          >
            <Typography variant="body2" textAlign="start" fontWeight={700}>
              Enable Notifications
            </Typography>
          </Button>
        </Stack>
      );
    }
    return (
      <Stack
        direction="column"
        sx={{
          gap: 2,
        }}
      >
        <Typography variant="subtitle1" textAlign="start" fontWeight={400}>
          To install the app, you need to add this website to your home screen
        </Typography>
        {Desc}
      </Stack>
    );
  }, [type, Desc]);

  return (
    <Dialog
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClose={() => {
        if (type === 'notification') {
          hideReminder(type);
          return;
        }
        hideReminder(type);
        // skip reminder if not enabled
        const enabled = process.env.REACT_APP_NOTIFICATION_ENABLED;
        if (enabled !== 'true') return;
        const isPushSupported = OneSignal.Notifications.isPushSupported();
        if (isPushSupported) {
          // check notification
          let permission =
            Notification.permission === 'granted' ||
            !!OneSignal.Notifications.permission ||
            OneSignal.Notifications.permissionNative === 'granted';
          if (!permission) showReminder('notification');
        }
      }}
    >
      <IconButton
        aria-label="close"
        onClick={() => {
          if (type === 'notification') {
            hideReminder(type);
            return;
          }
          hideReminder(type);
          // skip reminder if not enabled
          const enabled = process.env.REACT_APP_NOTIFICATION_ENABLED;
          if (enabled !== 'true') return;
          const isPushSupported = OneSignal.Notifications.isPushSupported();
          if (isPushSupported) {
            // check notification
            let permission =
              Notification.permission === 'granted' ||
              !!OneSignal.Notifications.permission ||
              OneSignal.Notifications.permissionNative === 'granted';
            if (!permission) showReminder('notification');
          }
        }}
        sx={{
          position: 'absolute',
          right: 12,
          top: 14,
          color: 'rgba(0, 0, 0, 0.8)',
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle id="alert-dialog-title">
        {type === 'notification' ? 'Allow Notification?' : 'Add to Home Screen'}
      </DialogTitle>
      <DialogContent>{Content}</DialogContent>
    </Dialog>
  );
};
